import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

type ThemeMode = 'light' | 'dark' | 'system';
type Sender = 'user' | 'assistant' | 'system';

interface ChatMessage {
  id: string;
  sender: Sender;
  text: string;
  pending?: boolean;
  streaming?: boolean;
}

interface RagResponse {
  answer?: string;
  response?: string;
  text?: string;
  message?: string;
}

interface QueryResult {
  answer: string;
}

interface StreamCallbacks {
  onToken: (token: string) => void;
}

interface SseEvent {
  event: 'token' | 'citations' | 'done' | 'error' | string;
  data: string;
}

const ENTER_KEY = 'Enter';
const SCROLL_LOCK_THRESHOLD_PX = 72;
const STREAM_RENDER_INTERVAL_MS = 24;

@customElement('vanx-rag-chat')
export class VanxRagChat extends LitElement {
  @property({ type: String, attribute: 'client-id' })
  clientId = '';

  @property({ type: String, attribute: 'api-url' })
  apiUrl = '';

  @property({ type: String, reflect: true })
  theme: ThemeMode = 'system';

  @property({ type: String, attribute: 'primary-color' })
  primaryColor = '#4f46e5';

  @property({ type: String })
  title = 'Vanx RAG Chat';

  @property({ type: String })
  placeholder = 'Ask a question...';

  @property({ type: String, attribute: 'welcome-message' })
  welcomeMessage =
    'Ask me anything. I will search your knowledge base and answer with the most relevant context.';

  @property({ type: String, attribute: 'ready-label' })
  readyLabel = 'Ready';

  @property({ type: String, attribute: 'loading-label' })
  loadingLabel = 'Searching knowledge';

  @property({ type: String, attribute: 'input-label' })
  inputLabel = 'Message';

  @property({ type: String, attribute: 'open-label' })
  openLabel = 'Open chat';

  @property({ type: String, attribute: 'close-label' })
  closeLabel = 'Close chat';

  @property({ type: String, attribute: 'send-label' })
  sendLabel = 'Send message';

  @property({ type: String, attribute: 'typing-label' })
  typingLabel = 'Assistant is typing';

  @property({ type: String, attribute: 'empty-answer-message' })
  emptyAnswerMessage = 'I did not receive an answer for that question.';

  @property({ type: String, attribute: 'missing-api-url-message' })
  missingApiUrlMessage = 'Missing api-url attribute.';

  @property({ type: String, attribute: 'request-error-message' })
  requestErrorMessage = 'The request failed. Please try again.';

  @state()
  private isOpen = false;

  @state()
  private inputValue = '';

  @state()
  private isSending = false;

  @state()
  private messages: ChatMessage[] = [
    {
      id: crypto.randomUUID(),
      sender: 'assistant',
      text: '',
    },
  ];

  private pendingTokenBuffer = '';

  private streamingRenderInterval?: number;

  private activeAssistantMessageId?: string;

  private shouldAutoScroll = true;

  connectedCallback(): void {
    super.connectedCallback();
    this.messages = [
      {
        id: crypto.randomUUID(),
        sender: 'assistant',
        text: this.welcomeMessage,
      },
    ];
  }

  static styles = css`
    :host {
      --vanx-primary: #4f46e5;
      --rag-primary-color: var(--vanx-primary);
      --vanx-primary-contrast: #ffffff;
      --vanx-bg: #0f172a;
      --vanx-bg-soft: #111827;
      --vanx-layer: #1e293b;
      --vanx-layer-soft: #172033;
      --vanx-surface: rgba(15, 23, 42, 0.92);
      --vanx-border: rgba(148, 163, 184, 0.18);
      --vanx-text: #e5edf7;
      --vanx-muted: #98a6ba;
      --vanx-shadow:
        0 32px 88px rgba(2, 6, 23, 0.48),
        0 12px 30px rgba(15, 23, 42, 0.28);
      --vanx-ring: color-mix(in srgb, var(--vanx-primary) 26%, transparent);
      position: fixed;
      right: 24px;
      bottom: 24px;
      z-index: 2147483000;
      display: block;
      color: var(--vanx-text);
      font-size: 14px;
      line-height: 1.5;
      font-family:
        Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
        "Segoe UI", sans-serif;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
    }

    :host([theme='dark']) {
      --vanx-bg: #0f172a;
      --vanx-bg-soft: #111827;
      --vanx-layer: #1e293b;
      --vanx-layer-soft: #172033;
      --vanx-surface: rgba(15, 23, 42, 0.92);
      --vanx-border: rgba(148, 163, 184, 0.18);
      --vanx-text: #e5edf7;
      --vanx-muted: #98a6ba;
      --vanx-shadow:
        0 32px 88px rgba(2, 6, 23, 0.48),
        0 12px 30px rgba(15, 23, 42, 0.28);
    }

    :host([theme='light']) {
      --vanx-bg: #ffffff;
      --vanx-bg-soft: #f8fafc;
      --vanx-layer: #ffffff;
      --vanx-layer-soft: #f1f5f9;
      --vanx-surface: rgba(255, 255, 255, 0.9);
      --vanx-border: rgba(15, 23, 42, 0.12);
      --vanx-text: #0f172a;
      --vanx-muted: #64748b;
      --vanx-shadow:
        0 32px 88px rgba(15, 23, 42, 0.22),
        0 12px 30px rgba(15, 23, 42, 0.12);
    }

    @media (prefers-color-scheme: dark) {
      :host([theme='system']) {
        --vanx-bg: #0f172a;
        --vanx-bg-soft: #111827;
        --vanx-layer: #1e293b;
        --vanx-layer-soft: #172033;
        --vanx-surface: rgba(15, 23, 42, 0.92);
        --vanx-border: rgba(148, 163, 184, 0.18);
        --vanx-text: #e5edf7;
        --vanx-muted: #98a6ba;
        --vanx-shadow:
          0 32px 88px rgba(2, 6, 23, 0.48),
          0 12px 30px rgba(15, 23, 42, 0.28);
      }
    }

    * {
      box-sizing: border-box;
    }

    button,
    textarea {
      font: inherit;
    }

    .launcher {
      width: 64px;
      height: 64px;
      display: grid;
      place-items: center;
      border: 0;
      border-radius: 999px;
      color: var(--vanx-primary-contrast);
      background:
        radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.42), transparent 28%),
        linear-gradient(135deg, color-mix(in srgb, var(--vanx-primary) 88%, white), var(--vanx-primary));
      box-shadow:
        0 18px 44px color-mix(in srgb, var(--vanx-primary) 38%, transparent),
        0 8px 18px rgba(2, 6, 23, 0.22);
      cursor: pointer;
      transition:
        transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1),
        box-shadow 220ms ease,
        opacity 180ms ease,
        filter 180ms ease;
    }

    .launcher:hover {
      transform: translateY(-4px) scale(1.045);
      filter: saturate(1.08);
      box-shadow:
        0 24px 60px color-mix(in srgb, var(--vanx-primary) 46%, transparent),
        0 12px 24px rgba(2, 6, 23, 0.26);
    }

    .launcher:focus-visible,
    .send:focus-visible {
      outline: 3px solid var(--vanx-ring);
      outline-offset: 3px;
    }

    .launcher svg {
      width: 28px;
      height: 28px;
    }

    .panel {
      position: absolute;
      right: 0;
      bottom: 84px;
      width: min(430px, calc(100vw - 32px));
      height: min(700px, calc(100vh - 116px));
      display: grid;
      grid-template-rows: auto 1fr auto;
      min-height: 0;
      overflow: hidden;
      border: 1px solid var(--vanx-border);
      border-radius: 24px;
      background:
        linear-gradient(180deg, rgba(255, 255, 255, 0.035), transparent 42%),
        var(--vanx-surface);
      box-shadow: var(--vanx-shadow);
      backdrop-filter: blur(22px) saturate(1.32);
      transform: translateY(16px) scale(0.95);
      transform-origin: bottom right;
      opacity: 0;
      pointer-events: none;
      transition:
        transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1),
        opacity 180ms ease;
    }

    .panel.open {
      transform: translateY(0) scale(1);
      opacity: 1;
      pointer-events: auto;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
      padding: 14px 16px;
      border-bottom: 1px solid var(--vanx-border);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--vanx-layer) 58%, transparent), transparent),
        var(--vanx-surface);
    }

    .identity {
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .mark {
      width: 34px;
      height: 34px;
      display: grid;
      flex: 0 0 auto;
      place-items: center;
      border-radius: 12px;
      color: var(--vanx-primary-contrast);
      background: var(--vanx-primary);
      box-shadow: 0 12px 28px color-mix(in srgb, var(--vanx-primary) 32%, transparent);
    }

    .title {
      margin: 0;
      overflow: hidden;
      color: var(--vanx-text);
      font-size: 15px;
      font-weight: 700;
      line-height: 1.2;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .status {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 3px;
      color: var(--vanx-muted);
      font-size: 12px;
      font-weight: 500;
      line-height: 1.2;
    }

    .pulse {
      width: 7px;
      height: 7px;
      border-radius: 999px;
      background: #22c55e;
      box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.14);
    }

    .close {
      width: 36px;
      height: 36px;
      display: grid;
      place-items: center;
      border: 1px solid var(--vanx-border);
      border-radius: 12px;
      color: var(--vanx-muted);
      background: color-mix(in srgb, var(--vanx-layer-soft) 72%, transparent);
      cursor: pointer;
      transition:
        background 160ms ease,
        color 160ms ease,
        transform 160ms ease;
    }

    .close:hover {
      color: var(--vanx-text);
      background: var(--vanx-layer);
      transform: translateY(-1px);
    }

    .messages {
      min-height: 0;
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 14px;
      overflow-y: auto;
      overscroll-behavior: contain;
      scroll-behavior: smooth;
      background:
        radial-gradient(circle at 50% 0, color-mix(in srgb, var(--vanx-primary) 10%, transparent), transparent 34%),
        linear-gradient(180deg, transparent, color-mix(in srgb, var(--vanx-bg-soft) 72%, transparent)),
        var(--vanx-bg);
      scrollbar-color: color-mix(in srgb, var(--vanx-muted) 42%, transparent) transparent;
      scrollbar-width: thin;
    }

    .messages::-webkit-scrollbar {
      width: 10px;
    }

    .messages::-webkit-scrollbar-track {
      background: transparent;
    }

    .messages::-webkit-scrollbar-thumb {
      border: 3px solid transparent;
      border-radius: 999px;
      background:
        linear-gradient(
            color-mix(in srgb, var(--vanx-muted) 42%, transparent),
            color-mix(in srgb, var(--vanx-muted) 42%, transparent)
          )
          padding-box;
    }

    .messages::-webkit-scrollbar-thumb:hover {
      background:
        linear-gradient(
            color-mix(in srgb, var(--vanx-muted) 58%, transparent),
            color-mix(in srgb, var(--vanx-muted) 58%, transparent)
          )
          padding-box;
    }

    .message {
      display: grid;
      flex: 0 0 auto;
      min-width: 0;
      max-width: 86%;
      animation: message-in 180ms ease both;
    }

    .message.user {
      align-self: end;
      max-width: 82%;
    }

    .message.assistant,
    .message.system {
      align-self: start;
      max-width: 94%;
    }

    .bubble {
      border: 1px solid var(--vanx-border);
      border-radius: 16px;
      padding: 8px 11px;
      font-size: 14px;
      line-height: 1.42;
      letter-spacing: 0;
      text-align: left;
      max-width: 100%;
      overflow-x: hidden;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    .message-text {
      display: inline;
      margin: 0;
      padding: 0;
      white-space: pre-wrap;
    }

    .rag-stream-cursor {
      display: inline-block;
      margin-left: 2px;
      color: var(--rag-primary-color);
      font-weight: 700;
      opacity: 1;
      transform: translateY(-1px);
      animation: ragCursorPulse 1s ease-in-out infinite;
      filter: drop-shadow(0 0 6px rgba(59, 130, 246, 0.45));
    }

    .user .bubble {
      border-color: transparent;
      border-bottom-right-radius: 6px;
      color: var(--vanx-primary-contrast);
      background:
        linear-gradient(135deg, color-mix(in srgb, var(--vanx-primary) 88%, white), var(--vanx-primary));
      box-shadow: 0 12px 30px color-mix(in srgb, var(--vanx-primary) 24%, transparent);
    }

    .assistant .bubble,
    .system .bubble {
      border-bottom-left-radius: 6px;
      color: var(--vanx-text);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--vanx-layer) 86%, transparent), color-mix(in srgb, var(--vanx-layer-soft) 90%, transparent));
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
    }

    .rag-thinking {
      display: flex;
      gap: 4px;
      align-items: center;
    }

    .rag-thinking span {
      width: 6px;
      height: 6px;
      border-radius: 999px;
      background: var(--rag-primary-color);
      animation: ragThinking 1.4s infinite ease-in-out;
    }

    .rag-thinking span:nth-child(2) {
      animation-delay: 0.15s;
    }

    .rag-thinking span:nth-child(3) {
      animation-delay: 0.3s;
    }

    .composer {
      padding: 10px;
      border-top: 1px solid var(--vanx-border);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--vanx-layer-soft) 36%, transparent), transparent),
        var(--vanx-surface);
    }

    .composer-shell {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 36px;
      gap: 8px;
      align-items: end;
      border: 1px solid var(--vanx-border);
      border-radius: 24px;
      padding: 8px;
      background: color-mix(in srgb, var(--vanx-layer-soft) 82%, transparent);
      transition:
        border-color 160ms ease,
        box-shadow 160ms ease;
    }

    .composer-shell:focus-within {
      border-color: color-mix(in srgb, var(--vanx-primary) 48%, var(--vanx-border));
      box-shadow: 0 0 0 4px var(--vanx-ring);
    }

    textarea {
      width: 100%;
      min-width: 0;
      max-height: 128px;
      min-height: 36px;
      display: block;
      resize: none;
      border: 0;
      outline: 0;
      margin: 0;
      padding: 7px 0;
      color: var(--vanx-text);
      background: transparent;
      font-size: 14px;
      line-height: 1.42;
      letter-spacing: 0;
    }

    textarea::placeholder {
      color: var(--vanx-muted);
    }

    .send {
      width: 36px;
      height: 36px;
      flex: 0 0 auto;
      display: grid;
      place-items: center;
      border: 0;
      border-radius: 14px;
      color: var(--vanx-primary-contrast);
      background:
        linear-gradient(135deg, color-mix(in srgb, var(--vanx-primary) 90%, white), var(--vanx-primary));
      cursor: pointer;
      transition:
        transform 160ms ease,
        opacity 160ms ease,
        filter 160ms ease;
    }

    .send:hover:not(:disabled) {
      transform: translateY(-1px);
      filter: brightness(1.04);
    }

    .send:disabled {
      cursor: not-allowed;
      opacity: 0.44;
    }

    @keyframes message-in {
      from {
        transform: translateY(5px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @keyframes typing {
      0%,
      80%,
      100% {
        transform: translateY(0);
        opacity: 0.45;
      }
      40% {
        transform: translateY(-4px);
        opacity: 1;
      }
    }

    @keyframes ragThinking {
      0%,
      80%,
      100% {
        opacity: 0.25;
        transform: scale(0.8);
      }

      40% {
        opacity: 1;
        transform: scale(1);
      }
    }

    @keyframes ragCursorPulse {
      0%,
      100% {
        opacity: 1;
        transform: translateY(-1px) scaleY(1);
      }

      50% {
        opacity: 0.15;
        transform: translateY(-1px) scaleY(0.92);
      }
    }

    @media (max-width: 520px) {
      :host {
        right: 0;
        bottom: 16px;
        left: 0;
        display: flex;
        justify-content: flex-end;
        padding: 0 16px;
      }

      .panel {
        right: 0;
        left: 0;
        bottom: 72px;
        width: 100vw;
        height: calc(100vh - 88px);
        max-height: 100vh;
        border-right: 0;
        border-left: 0;
        border-radius: 22px 22px 0 0;
      }

      .launcher {
        width: 58px;
        height: 58px;
      }
    }
  `;

  protected updated(): void {
    this.style.setProperty('--vanx-primary', this.primaryColor || '#4f46e5');
    this.scrollMessagesToBottom();
  }

  protected firstUpdated(): void {
    const messages = this.renderRoot.querySelector<HTMLElement>('#messages');
    messages?.addEventListener('scroll', this.handleMessagesScroll, { passive: true });
  }

  disconnectedCallback(): void {
    this.stopStreamingRenderer();

    const messages = this.renderRoot.querySelector<HTMLElement>('#messages');
    messages?.removeEventListener('scroll', this.handleMessagesScroll);

    super.disconnectedCallback();
  }

  render() {
    const canSend = this.inputValue.trim().length > 0 && !this.isSending;

    return html`
      <section
        class=${classMap({ panel: true, open: this.isOpen })}
        aria-label=${this.title}
      >
        <header class="header">
          <div class="identity">
            <div class="mark" aria-hidden="true">${this.sparkIcon()}</div>
            <div>
              <h2 class="title">${this.title}</h2>
              <div class="status">
                <span class="pulse" aria-hidden="true"></span>
                <span>${this.isSending ? this.loadingLabel : this.readyLabel}</span>
              </div>
            </div>
          </div>
          <button class="close" type="button" @click=${this.closePanel} aria-label=${this.closeLabel}>
            ${this.closeIcon()}
          </button>
        </header>

        <div class="messages" id="messages" role="log" aria-live="polite">
          ${this.messages.map((message) => this.renderMessage(message))}
        </div>

        <form class="composer" @submit=${this.handleSubmit}>
          <div class="composer-shell">
            <textarea
              .value=${this.inputValue}
              ?disabled=${this.isSending}
              rows="1"
              placeholder=${this.placeholder}
              aria-label=${this.inputLabel}
              @input=${this.handleInput}
              @keydown=${this.handleKeydown}
            ></textarea>
            <button class="send" type="submit" ?disabled=${!canSend} aria-label=${this.sendLabel}>
              ${this.sendIcon()}
            </button>
          </div>
        </form>
      </section>

      <button
        class="launcher"
        type="button"
        @click=${this.togglePanel}
        aria-label=${this.isOpen ? this.closeLabel : this.openLabel}
        aria-expanded=${String(this.isOpen)}
      >
        ${this.isOpen ? this.closeIcon() : this.chatIcon()}
      </button>
    `;
  }

  private renderMessage(message: ChatMessage) {
    return html`
      <article class=${classMap({ message: true, [message.sender]: true })}>
        <div class="bubble">
          ${message.pending
            ? html`<div class="rag-thinking" aria-label=${this.typingLabel}>
                <span></span>
                <span></span>
                <span></span>
              </div>`
            : html`<span class="message-text"
                  >${this.normalizeDisplayText(message.text)}${message.streaming
                    ? html`${'\u2060'}<span class="rag-stream-cursor">▋</span>`
                    : nothing}</span
                >`}
        </div>
      </article>
    `;
  }

  private togglePanel = () => {
    this.isOpen = !this.isOpen;
  };

  private closePanel = () => {
    this.isOpen = false;
  };

  private handleInput = (event: Event) => {
    this.inputValue = (event.target as HTMLTextAreaElement).value;
  };

  private handleKeydown = (event: KeyboardEvent) => {
    if (event.key === ENTER_KEY && !event.shiftKey) {
      event.preventDefault();
      void this.submitQuestion();
    }
  };

  private handleSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    void this.submitQuestion();
  };

  private async submitQuestion(): Promise<void> {
    const question = this.inputValue.trim();

    if (!question || this.isSending) {
      return;
    }

    this.inputValue = '';
    this.isSending = true;
    this.shouldAutoScroll = true;

    const assistantId = crypto.randomUUID();
    this.pendingTokenBuffer = '';
    this.activeAssistantMessageId = assistantId;
    this.messages = [
      ...this.messages,
      { id: crypto.randomUUID(), sender: 'user', text: question },
      { id: assistantId, sender: 'assistant', text: '', pending: true },
    ];

    try {
      const result = await this.queryRag(question, {
        onToken: (token) => {
          this.appendAssistantChunk(assistantId, token);
        },
      });

      await this.drainPendingTokens(assistantId);

      if (result.answer) {
        this.replaceMessage(assistantId, {
          text: this.getMessageText(assistantId) || result.answer,
          pending: false,
          streaming: false,
        });
      } else {
        this.replaceMessage(assistantId, {
          text: this.emptyAnswerMessage,
          pending: false,
          streaming: false,
        });
      }
    } catch (error) {
      this.replaceMessage(assistantId, {
        text: error instanceof Error ? error.message : this.requestErrorMessage,
        pending: false,
        streaming: false,
      });
    } finally {
      this.stopStreamingRenderer();
      this.activeAssistantMessageId = undefined;
      this.isSending = false;
    }
  }

  private async queryRag(
    question: string,
    callbacks: StreamCallbacks,
  ): Promise<QueryResult> {
    if (!this.apiUrl) {
      throw new Error(this.missingApiUrlMessage);
    }

    const response = await fetch(`${this.apiUrl.replace(/\/$/, '')}/rag/query-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        clientId: this.clientId,
        question,
      }),
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}.`);
    }

    if (!response.body) {
      throw new Error(this.requestErrorMessage);
    }

    return this.readSseStream(response.body, callbacks);
  }

  private async readSseStream(
    body: ReadableStream<Uint8Array>,
    callbacks: StreamCallbacks,
  ): Promise<QueryResult> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let answer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const parsed = this.extractSseEvents(buffer);
      buffer = parsed.remainder;

      for (const event of parsed.events) {
        const result = this.handleSseEvent(event, callbacks);
        answer += result.token;

        if (result.done) {
          await reader.cancel();
          return {
            answer: this.normalizeDisplayText(answer),
          };
        }
      }
    }

    buffer += decoder.decode();

    if (buffer.trim()) {
      for (const event of this.extractSseEvents(`${buffer}\n\n`).events) {
        const result = this.handleSseEvent(event, callbacks);
        answer += result.token;
      }
    }

    return {
      answer: this.normalizeDisplayText(answer),
    };
  }

  private extractSseEvents(buffer: string): { events: SseEvent[]; remainder: string } {
    const normalized = buffer.replace(/\r\n/g, '\n');
    const parts = normalized.split('\n\n');
    const remainder = parts.pop() ?? '';
    const events = parts
      .map((part) => this.parseSseEvent(part))
      .filter((event) => event.data || event.event !== 'message');

    return { events, remainder };
  }

  private parseSseEvent(rawEvent: string): SseEvent {
    const lines = rawEvent.split('\n');
    let event = 'message';
    const data: string[] = [];

    for (const line of lines) {
      if (!line || line.startsWith(':')) {
        continue;
      }

      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        data.push(line.slice(5).trimStart());
      }
    }

    return {
      event,
      data: data.join('\n'),
    };
  }

  private handleSseEvent(
    event: SseEvent,
    callbacks: StreamCallbacks,
  ): { token: string; done?: boolean } {
    if (event.event === 'done') {
      return { token: '', done: true };
    }

    if (event.event === 'error') {
      throw new Error(this.getSseErrorMessage(event.data));
    }

    if (event.event === 'citations') {
      return { token: '' };
    }

    if (event.event === 'token' || event.event === 'message') {
      const token = this.getSseToken(event.data);
      callbacks.onToken(token);
      return { token };
    }

    return { token: '' };
  }

  private getResponseAnswer(response: RagResponse): string {
    return this.normalizeDisplayText(
      response.answer ?? response.response ?? response.text ?? response.message ?? '',
    );
  }

  private getSseToken(data: string): string {
    if (data === '[DONE]') {
      return '';
    }

    try {
      const parsed = JSON.parse(data) as RagResponse & {
        token?: string;
        delta?: string;
        content?: string;
      };

      return parsed.token ?? parsed.delta ?? parsed.content ?? this.getResponseAnswer(parsed);
    } catch {
      return data;
    }
  }

  private getSseErrorMessage(data: string): string {
    try {
      const parsed = JSON.parse(data) as { error?: string; message?: string };
      return parsed.error ?? parsed.message ?? this.requestErrorMessage;
    } catch {
      return data || this.requestErrorMessage;
    }
  }

  private normalizeDisplayText(text: string): string {
    return text.trim();
  }

  private appendAssistantChunk(id: string, chunk: string): void {
    if (!chunk) {
      return;
    }

    this.pendingTokenBuffer += chunk;
    this.activeAssistantMessageId = id;
    this.startStreamingRenderer();
  }

  private startStreamingRenderer(): void {
    if (this.streamingRenderInterval !== undefined) {
      return;
    }

    this.streamingRenderInterval = window.setInterval(() => {
      if (!this.activeAssistantMessageId) {
        this.stopStreamingRenderer();
        return;
      }

      this.renderNextPendingTokenPiece(this.activeAssistantMessageId);
    }, STREAM_RENDER_INTERVAL_MS);
  }

  private stopStreamingRenderer(): void {
    if (this.streamingRenderInterval === undefined) {
      return;
    }

    window.clearInterval(this.streamingRenderInterval);
    this.streamingRenderInterval = undefined;
  }

  private renderNextPendingTokenPiece(id: string): void {
    if (!this.pendingTokenBuffer) {
      return;
    }

    const piece = this.takeNextTokenPiece();
    const existing = this.messages.find((message) => message.id === id);

    this.replaceMessage(id, {
      text: `${existing?.text ?? ''}${piece}`,
      pending: false,
      streaming: true,
    });
  }

  private takeNextTokenPiece(): string {
    const preferredSize = this.getNextTokenPieceSize();
    const piece = this.pendingTokenBuffer.slice(0, preferredSize);
    this.pendingTokenBuffer = this.pendingTokenBuffer.slice(piece.length);

    return piece;
  }

  private getNextTokenPieceSize(): number {
    const buffer = this.pendingTokenBuffer;

    if (buffer.length <= 4) {
      return buffer.length;
    }

    const match = buffer.match(/^(\s+|[\wÀ-ÿ]+(?:['-][\wÀ-ÿ]+)?|[^\s\wÀ-ÿ]+)/u);
    const tokenLength = match?.[0]?.length ?? 1;

    if (tokenLength <= 4) {
      return tokenLength;
    }

    return Math.min(tokenLength, 7);
  }

  private async drainPendingTokens(id: string): Promise<void> {
    while (this.pendingTokenBuffer) {
      this.renderNextPendingTokenPiece(id);
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, STREAM_RENDER_INTERVAL_MS);
      });
    }

    this.stopStreamingRenderer();
  }

  private getMessageText(id: string): string {
    return this.messages.find((message) => message.id === id)?.text ?? '';
  }

  private replaceMessage(id: string, patch: Partial<ChatMessage>): void {
    this.messages = this.messages.map((message) =>
      message.id === id ? { ...message, ...patch } : message,
    );
  }


  private scrollMessagesToBottom(): void {
    if (!this.shouldAutoScroll) {
      return;
    }

    const messages = this.renderRoot.querySelector<HTMLElement>('#messages');
    messages?.scrollTo({ top: messages.scrollHeight, behavior: 'smooth' });
  }

  private handleMessagesScroll = (event: Event): void => {
    const messages = event.currentTarget as HTMLElement;
    const distanceFromBottom =
      messages.scrollHeight - messages.scrollTop - messages.clientHeight;

    this.shouldAutoScroll = distanceFromBottom <= SCROLL_LOCK_THRESHOLD_PX;
  };

  private chatIcon() {
    return html`
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M5 17.25V7.5A3.5 3.5 0 0 1 8.5 4h7A3.5 3.5 0 0 1 19 7.5v5A3.5 3.5 0 0 1 15.5 16H9.25L5 19.5v-2.25Z"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M8.5 9h7M8.5 12h4.5"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
    `;
  }

  private closeIcon() {
    return html`
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="m7 7 10 10M17 7 7 17"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
    `;
  }

  private sendIcon() {
    return html`
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="m5 12 14-7-4.5 14-2.75-5.75L5 12Z"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
  }

  private sparkIcon() {
    return html`
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 3.75 13.9 9l5.35 1.75-5.35 1.8L12 17.75l-1.9-5.2-5.35-1.8L10.1 9 12 3.75Z"
          fill="currentColor"
        />
      </svg>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vanx-rag-chat': VanxRagChat;
  }
}
